import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getImpersonateId } from './user';
import { signOut } from 'store/user';

import { CompletedSurvey } from '../types/Survey';
import { getKits } from './shared';
import { RootState } from './store';
import { getConsultCreditsCount, getTinyAccounts } from './account';
import { Kit, KitStatusValues, KitType, KitVariant } from '@repo/types';
import {
  kitResultsViewable,
  sortKitsByLatestSampleDate,
  sortKitsByResultsViewableOrLatest,
} from '@repo/utils';

interface KitsState {
  kits: Kit[];
  pdfRequests: { [key: string]: boolean };
}

export const initialState: KitsState = {
  kits: [],
  pdfRequests: {},
};

// Reducer
const kitsSlice = createSlice({
  name: 'kits',
  initialState,
  reducers: {
    setKits(state, action: PayloadAction<Kit[]>) {
      state.kits = action.payload;
    },
    setRequestedPDFForKit(state, action: PayloadAction<string>) {
      state.pdfRequests[action.payload] = true;
    },
    setSurveyCompleted(state, action: PayloadAction<CompletedSurvey>) {
      const { form_id, kit_id } = action.payload;
      const kitToUpdate = state.kits.find(kit => kit.id === kit_id);
      if (kitToUpdate?.survey_status) {
        const surveyIndex = kitToUpdate.survey_status.pending.findIndex(
          survey => survey.typeform_id === form_id,
        );
        if (surveyIndex !== -1) {
          const movedSurvey = kitToUpdate.survey_status.pending.splice(
            surveyIndex,
            1,
          )[0];
          if (movedSurvey) {
            kitToUpdate.survey_status.completed.push(movedSurvey);
          }
        }
      }
    },
    revertSurveyCompleted(state, action: PayloadAction<CompletedSurvey>) {
      const { form_id, kit_id } = action.payload;
      const kitToUpdate = state.kits.find(kit => kit.id === kit_id);
      if (kitToUpdate?.survey_status) {
        const surveyIndex = kitToUpdate.survey_status.completed.findIndex(
          survey => survey.typeform_id === form_id,
        );
        if (surveyIndex !== -1) {
          const movedSurvey = kitToUpdate.survey_status.completed.splice(
            surveyIndex,
            1,
          )[0];
          if (movedSurvey) {
            kitToUpdate.survey_status.pending.push(movedSurvey);
          }
        }
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(signOut, (state, action) => {
      return initialState;
    });
  },
});

// Selectors
export const getKitsState = (state: RootState) => state.kits;

export const getLatestKitOfThisAccount = () =>
  createSelector(getKits, kits => {
    const nonVoidKits = kits.filter(kit => kit.status !== KitStatusValues.VOID);
    if (!nonVoidKits.length) return null;
    const sortedKits = sortKitsByLatestSampleDate(nonVoidKits);
    return sortedKits[0];
  });

export const getAllActivatedNonVoidedKits = () =>
  createSelector(getKits, kits =>
    kits.filter(kit => {
      const isActivated = !!kit.date_activated;
      const isVoidedByDate = !!kit.date_voided;
      const isVoidedByStatus = kit.status === KitStatusValues.VOID;
      return isActivated && !isVoidedByDate && !isVoidedByStatus;
    }),
  );

export const getLatestActivatedNonVoidedKit = () =>
  createSelector(getKits, kits => {
    const eligibleKits = kits.filter(kit => {
      const isVoidedByDate = !!kit.date_voided;
      const isVoidedByStatus = kit.status === KitStatusValues.VOID;
      const isActivated = !!kit.date_activated;
      return isActivated && !isVoidedByDate && !isVoidedByStatus;
    });

    if (!eligibleKits.length) return null;

    const sorted = [...eligibleKits].sort((a, b) => {
      const aDate = a.date_activated ? new Date(a.date_activated).getTime() : 0;
      const bDate = b.date_activated ? new Date(b.date_activated).getTime() : 0;
      return bDate - aDate;
    });

    return sorted[0];
  });

export const getKitsForTinyAccountId = (tinyAccountId: string | null) =>
  createSelector(getKits, kits =>
    kits.filter(kit => kit.tinyaccount_id === tinyAccountId),
  );

export const getSortedKitsForTinyAccountId = (tinyAccountId: string | null) =>
  createSelector(getKitsForTinyAccountId(tinyAccountId), kits => {
    if (!tinyAccountId || !kits) return [];
    return sortKitsByLatestSampleDate(kits);
  });
export const getLatestKitForTinyAccountId = (tinyAccountId: string | null) =>
  createSelector(getSortedKitsForTinyAccountId(tinyAccountId), kits => {
    if (!kits || !kits.length) return null;
    return kits[0];
  });
export const getResultsReadyKitForTinyAccountId = (
  tinyAccountId: string | null,
) =>
  createSelector(
    [getKitsForTinyAccountId(tinyAccountId), getImpersonateId],
    (kits, impersonateId) => {
      if (!tinyAccountId || !kits || !kits.length) return null;
      const sorted = sortKitsByResultsViewableOrLatest(kits);
      const firstResultsReady = sorted.find(kit =>
        kitResultsViewable(kit, !!impersonateId),
      );
      return firstResultsReady ?? sorted[0] ?? null;
    },
  );

export const getLatestKits = () =>
  createSelector([getTinyAccounts, getKits], (tinyAccounts, kits) => {
    const latestKits: Kit[] = [];
    tinyAccounts?.forEach(tinyAccount => {
      const thisAccountKits = kits.filter(
        kit => kit.tinyaccount_id === tinyAccount.id,
      );
      const sortedKitsForThisAccount =
        sortKitsByLatestSampleDate(thisAccountKits);
      if (sortedKitsForThisAccount[0]) {
        //get the latest (first) vaginal and gut test
        const firstKit = sortedKitsForThisAccount[0];
        latestKits.push(firstKit);
        const savedType = firstKit.sampling_type;
        //get the first of the other type
        for (let i = 0; i < sortedKitsForThisAccount.length; i++) {
          const kit = sortedKitsForThisAccount[i];
          if (!kit) continue;
          if (sortedKitsForThisAccount[i]?.sampling_type !== savedType) {
            latestKits.push(kit);
            break;
          }
        }
      }
    });
    return latestKits;
  });

export const getKitById = (kitId?: string) =>
  createSelector(getKits, kits => kits.find(kit => kit.id === kitId));

export const getKitHasResultsById = (kitId: string) =>
  createSelector([getKitById(kitId), getImpersonateId], (kit, impersonateId) =>
    kitResultsViewable(kit, !!impersonateId),
  );
export const getKitIsProKitById = (kitId?: string) =>
  createSelector(
    getKitById(kitId),
    kit =>
      !!kit?.variants?.find(
        (variant: KitVariant) => variant.sampling_type === KitType.STOOL,
      ),
  );

export const getSurveysStatusForKitId = (kitId?: string) =>
  createSelector(getKitById(kitId), kit => kit?.survey_status);

export const getPdfUrlForKitId = (kitId?: string) =>
  createSelector(getKitById(kitId), kit => kit?.pdf_url);

export const getKitBelongsToAccount = (kitId?: string | null) =>
  createSelector(getKits, kits => !!kits.find(kit => kit.id === kitId));

export const getKitPDFRequested = (kitId?: string | null) =>
  createSelector(getKitsState, state => kitId && !!state.pdfRequests[kitId]);

export const getBabyGutKitsForTinyAccountId = (tinyAccountId: string | null) =>
  createSelector(
    [getKitsForTinyAccountId(tinyAccountId), getImpersonateId],
    (kits, impersonateId) => {
      if (!tinyAccountId || !kits || !kits.length) return [];
      return kits.filter(
        (kit: Kit) =>
          kit.sampling_type === 'stool' &&
          kit.age_at_sampling &&
          kit.age_at_sampling < 395 &&
          kitResultsViewable(kit, !!impersonateId),
      );
    },
  );

export const getKitsForMicrobiomeBarList = (kitId?: string) =>
  createSelector(
    [getKitById(kitId), getKits, getImpersonateId],
    (currentKit, kits, impersonateId) => {
      const filteredKits = kits.filter(
        (kit: Kit) =>
          kit.tinyaccount_id === currentKit?.tinyaccount_id &&
          kit.sampling_type === currentKit?.sampling_type &&
          kitResultsViewable(kit, !!impersonateId) &&
          kit?.sample_date &&
          currentKit?.sample_date &&
          kit?.sample_date <= currentKit?.sample_date,
      );
      return sortKitsByLatestSampleDate(filteredKits);
    },
  );
export const getHasPractitionerHideConsult = (kitId?: string) =>
  createSelector(getKitById(kitId), kit => !!kit?.practitioner?.hide_consults);
export const getHasPartnerHideConsult = (kitId?: string) =>
  createSelector(getKitById(kitId), kit => !!kit?.partner?.hide_consults);
export const getShouldHideConsults = (kitId?: string) =>
  createSelector(
    [
      getKitById(kitId),
      getHasPractitionerHideConsult(kitId),
      getConsultCreditsCount,
    ],
    (kit, hasPractitionerHideConsult, consultCreditsCount) => {
      if (kit?.partner) return !!kit.partner.hide_consults; //partner config can't be overridden
      //having consult credits override practitioner config
      return hasPractitionerHideConsult && !consultCreditsCount;
    },
  );

export const {
  setKits,
  setRequestedPDFForKit,
  setSurveyCompleted,
  revertSurveyCompleted,
} = kitsSlice.actions;
export default kitsSlice.reducer;
